import axios from 'axios'
import { parseDateUTC } from '@/utils/formatters'

export function redirectToLogin() {
    const url = new URL(import.meta.env.VITE_BASE_API_URL + "/login");

    if (import.meta.env.VITE_BETA === 'true') {
        url.searchParams.set("beta-ui", "1");
    }

    if (window.location.pathname !== "/") {
        url.searchParams.set("redirect_app", window.location.pathname);
    }

    window.location.href = url.toString();
}

export function paginateUrl(url, pagination) {
    if (pagination.page) {
        url += `&page=${encodeURIComponent(pagination.page)}`;
    }

    if (pagination.sortBy !== undefined && pagination.sortBy[0]) {
        const sort = pagination.sortBy[0];
        url += `&sort-by=${encodeURIComponent(sort.key)}`;
        url += `&sort-order=${encodeURIComponent(sort.order || 'asc')}`;
    }

    if (pagination.date) {
        url += `&date=${encodeURIComponent(pagination.date)}`;
    }

    if (pagination.filter) {
        url += `&filter=${encodeURIComponent(pagination.filter)}`;
    }

    if (pagination.itemsPerPage !== null && pagination.itemsPerPage !== undefined) {
        if (pagination.itemsPerPage > 0) {
            return url += `&items-per-page=${encodeURIComponent(pagination.itemsPerPage)}`;
        }

        if (pagination.itemsPerPage === -1 && pagination.total) {
            return url += `&items-per-page=${encodeURIComponent(pagination.total)}`;
        }
    }

    return url;
}

export function getLocalDiskName() {
    switch (import.meta.env.VITE_ENV) {
        case "production":
            return `vapor-concord-local-disk`;

        case "staging":
            return `vapor-concord-staging-local-disk`;
        case "local":
            return `concord-local-disk`;
        // case "uat":
        default:
            return `vapor-concord-uat-local-disk`;
    }
}

export async function vaporUpload(file, options = {}) {

    // Step 1 - get pre-signed POST data.
    const bucketName = getLocalDiskName();

    const response = await axios.post("/vapor/signed-storage-url", {
        'bucket': bucketName,
        'content_type': file.type
    })

    // Step 2 - upload the file to S3.
    let headers = response.data.headers;

    if ('Host' in headers) {
        delete headers.Host;
    }

    if (typeof options.progress === 'undefined') {
        options.progress = () => { };
    }

    const cancelToken = options.cancelToken || undefined;

    const awsS3 = axios.create({
        withCredentials: false,
    });

    await awsS3.put(response.data.url, file, {
        cancelToken: cancelToken,
        headers: headers,
        onUploadProgress: (progressEvent) => {
            options.progress(progressEvent.loaded / progressEvent.total);
        }
    });

    response.data.extension = file.name.split('.').pop();
    response.data.original = file.name;

    return response.data;
}

export function addTimeline(campaign) {
    if (campaign.launch_date && campaign.end_date) {
        let start = parseDateUTC(campaign.launch_date);
        let end = parseDateUTC(campaign.end_date);
        campaign.timeline = `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        campaign.duration = `${end.diff(start, 'days')} days`;

        return campaign;
    }

    if (campaign.launch_date) {
        campaign.timeline = parseDateUTC(campaign.launch_date).format('DD/MM/YYYY');
    }

    return campaign;
}

export const STATUS_FLOWS = {
    campaign: [
        { status: "draft", label: "Draft", color: "blue", icon: "$brush" },
        { status: "testing", label: "Testing", color: "accent", icon: "$science" },
        { status: "ready", label: "Ready", color: "blue", icon: "$done" },
        { status: "live", label: "Live", color: "green", icon: "$playArrow" },
        { status: "finished", label: "Finished", color: "blue", icon: "$check" },
    ],
    creative: [
        { status: "draft", label: "Draft", color: "blue", icon: "$brush" },
        { status: "processing", label: "Processing", color: "accent", icon: "$sync" },
        { status: "action required", label: "Action Required", color: "red", icon: "$report" },
        { status: "quality assurance", label: "QA", color: "accent", icon: "$imageSearch" },
        { status: "delivered", label: "Delivered", color: "green", icon: "$outgoingMail" },
        { status: "testing", label: "Testing", color: "accent", icon: "$science" },
        { status: "ready", label: "Ready", color: "blue", icon: "$done" },
        { status: "live", label: "Live", color: "green", icon: "$playArrow" },
        { status: "finished", label: "Finished", color: "blue", icon: "$check" },
    ],
    profile: [
        { status: "synchronising", label: "Synchronising", color: "accent", icon: "$sync" },
        { status: "action required", label: "Action Required", color: "red", icon: "$report" },
        { status: "ready", label: "Ready", color: "blue", icon: "$done" },
        { status: "testing", label: "Testing", color: "accent", icon: "$science" },
        { status: "live", label: "Live", color: "green", icon: "$playArrow" },
    ],
    mediaEntityFormat: [
        { status: "quality assurance", label: "QA", color: "accent", icon: "$imageSearch" },
        { status: "delivered", label: "Delivered", color: "green", icon: "$outgoingMail" },
        { status: "testing", label: "Testing", color: "accent", icon: "$science" },
        { status: "ready", label: "Ready", color: "blue", icon: "$done" },
        { status: "live", label: "Live", color: "green", icon: "$playArrow" },
        { status: "finished", label: "Finished", color: "blue", icon: "$check" },
    ],
};

// Build a lookup map from all STATUS_FLOWS entries (creative has the full superset)
const _statusLookup = Object.values(STATUS_FLOWS)
    .flat()
    .reduce((map, entry) => {
        if (!map[entry.status]) {
            map[entry.status] = { color: entry.color, icon: entry.icon, label: entry.label };
        }
        return map;
    }, {});

export function getStatusIcon(model) {
    const empty = { color: null, icon: null, label: null };
    if (model == null) return empty;
    return _statusLookup[model.status] || { color: 'grey', icon: '$helpOutline', label: model.status };
}
/**
 * Width (px) for a uniform status chip that fits the widest label among the
 * given models. Measured with the chip font so pills stay equal-width without
 * reserving space for labels that aren't on screen.
 */
export function getStatusChipWidth(models) {
    // Statuses repeat heavily across rows — only measure each label once
    const labels = [...new Set(
        (models || [])
            .filter(Boolean)
            .map(model => getStatusIcon(model).label)
            .filter(Boolean)
    )];
    if (!labels.length) return null;

    const canvas = getStatusChipWidth._canvas
        || (getStatusChipWidth._canvas = document.createElement('canvas'));
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return null;

    ctx.font = '11px Aeonik, sans-serif';
    const widest = Math.max(...labels.map(label => ctx.measureText(label).width));

    // chip inline padding + border + leading icon and its margin + letter-spacing slack
    return Math.ceil(widest) + 36;
}
